// GENERATED FILE. DO NOT EDIT.
//
// One method per operation the Map Studio API serves but the public contract
// does not publish — billing, membership, API keys, usage and account
// preferences. Generated from the composed internal spec by
// scripts/generate-openapi.sh.
//
// These are deliberately absent from @mountaya/client: they are in OpenAPI so
// this repository can generate types and a client for its own console, not so
// an integrator can reach them. Adding one to mountayaapp/schemas publishes it.
//
// Method names are the spec's operationIds verbatim and are frozen (ADR 0036).
// Edit the spec under backend/descriptions/openapi/api/, not this file.

import type { MountayaTransport, RequestOptions } from "@mountaya/client";

import type { operations } from "../schema/api";

/**
 * SuccessBody extracts the JSON body of an operation's 2xx response: its 200, else its 201,
 * else its 202.
 *
 * The platform wraps every success in an envelope whose `data` carries the payload, so
 * this resolves to the envelope rather than to the bare resource: callers read
 * `result.data`, which is what the wire actually sends, `null` on a write that removes
 * something and has nothing left to answer. A 202 is read like the other two: an operation
 * that starts work rather than finishing it names what it started at `data`, and that is
 * typed only if this reaches it.
 */
type SuccessBody<O> = O extends {
  responses: { 200: { content: { "application/json": infer B } } };
}
  ? B
  : O extends { responses: { 201: { content: { "application/json": infer C } } } }
    ? C
    : O extends { responses: { 202: { content: { "application/json": infer D } } } }
      ? D
      : unknown;

/** The instants in `Organization`. */
const organizationDates = { created_at: true, updated_at: true } as const;

/** The instants in `SubscriptionDiscount`. */
const subscriptionDiscountDates = { ends_at: true } as const;

/** The instants in `OrganizationSubscription`. */
const organizationSubscriptionDates = {
  current_period_end_at: true,
  discount: subscriptionDiscountDates,
} as const;

/** The instants in `OrganizationSubscriptionSummary`. */
const organizationSubscriptionSummaryDates = { api: organizationSubscriptionDates } as const;

/** The instants in `OrganizationAccount`. */
const organizationAccountDates = { subscription: organizationSubscriptionSummaryDates } as const;

/** The instants in `OrganizationEntitlementsChange`. */
const organizationEntitlementsChangeDates = { changed_at: true } as const;

/** The instants in `OrganizationEntitlementsRecord`. */
const organizationEntitlementsRecordDates = {
  last_change: organizationEntitlementsChangeDates,
} as const;

/** The instants in `OrganizationMember`. */
const organizationMemberDates = { joined_at: true } as const;

/** The instants in `SecretKey`. */
const secretKeyDates = { created_at: true, last_used_at: true, revoked_at: true } as const;

/** The instants in `StaffOrganization`. */
const staffOrganizationDates = { created_at: true } as const;

/**
 * Revoke a secret key.
 * DELETE /v1/organizations/{organization_id}/secret-keys/{key_id}
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const deleteOrganizationSecretKey = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly key_id: string;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["deleteOrganizationSecretKey"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: secretKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteOrganizationSecretKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/secret-keys/${encodeURIComponent(String(args.key_id))}`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Read what the signed-in user may use of Map Studio in their personal space.
 * GET /v1/me/personal-space
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getMyPersonalSpace = (
  transport: MountayaTransport,
  options?: RequestOptions,
): Promise<SuccessBody<operations["getMyPersonalSpace"]>> =>
  transport.request({
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getMyPersonalSpace",
    path: "/v1/me/personal-space",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Read the account behind an organization.
 * GET /v1/organizations/{organization_id}/account
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getOrganizationAccount = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["getOrganizationAccount"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: organizationAccountDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationAccount",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/account`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's entitlement record (staff only).
 * GET /v1/organizations/{organization_id}/entitlements
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getOrganizationEntitlements = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["getOrganizationEntitlements"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: organizationEntitlementsRecordDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationEntitlements",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/entitlements`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List all secret keys for the organization.
 * GET /v1/organizations/{organization_id}/secret-keys
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getOrganizationSecretKeys = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly query?:
      | NonNullable<operations["getOrganizationSecretKeys"]["parameters"]["query"]>
      | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["getOrganizationSecretKeys"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: [secretKeyDates] },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationSecretKeys",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/secret-keys`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Search every customer organization (staff only).
 * GET /v1/staff/organizations
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getStaffOrganizations = (
  transport: MountayaTransport,
  args: {
    readonly query?:
      | NonNullable<operations["getStaffOrganizations"]["parameters"]["query"]>
      | undefined;
  } = {},
  options?: RequestOptions,
): Promise<SuccessBody<operations["getStaffOrganizations"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: [staffOrganizationDates] },
    idempotent: true,
    method: "GET",
    operationId: "getStaffOrganizations",
    path: "/v1/staff/organizations",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Tell whether the signed-in user is a Mountaya staff member.
 * GET /v1/staff/status
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const getStaffStatus = (
  transport: MountayaTransport,
  options?: RequestOptions,
): Promise<SuccessBody<operations["getStaffStatus"]>> =>
  transport.request({
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getStaffStatus",
    path: "/v1/staff/status",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update a secret key name.
 * PATCH /v1/organizations/{organization_id}/secret-keys/{key_id}
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const patchOrganizationSecretKey = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly key_id: string;
    readonly body: operations["patchOrganizationSecretKey"]["requestBody"] extends {
      content: { "application/json": infer B };
    }
      ? B
      : never;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["patchOrganizationSecretKey"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: secretKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "PATCH",
    operationId: "patchOrganizationSecretKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/secret-keys/${encodeURIComponent(String(args.key_id))}`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a new secret key.
 * POST /v1/organizations/{organization_id}/secret-keys
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const postOrganizationSecretKey = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly body: operations["postOrganizationSecretKey"]["requestBody"] extends {
      content: { "application/json": infer B };
    }
      ? B
      : never;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["postOrganizationSecretKey"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: secretKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganizationSecretKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/secret-keys`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Hand an organization's ownership to another member.
 * POST /v1/organizations/{organization_id}/transfer-ownership
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const postOrganizationTransferOwnership = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly body: operations["postOrganizationTransferOwnership"]["requestBody"] extends {
      content: { "application/json": infer B };
    }
      ? B
      : never;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["postOrganizationTransferOwnership"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationMemberDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationTransferOwnership",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/transfer-ownership`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Replace an organization's entitlement override (staff only).
 * PUT /v1/organizations/{organization_id}/entitlements
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const putOrganizationEntitlements = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly body: operations["putOrganizationEntitlements"]["requestBody"] extends {
      content: { "application/json": infer B };
    }
      ? B
      : never;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
    /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
    readonly ifMatch?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["putOrganizationEntitlements"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationEntitlementsRecordDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PUT",
    operationId: "putOrganizationEntitlements",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/entitlements`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Replace an organization's controlled settings (staff only).
 * PUT /v1/organizations/{organization_id}/settings
 * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
 */
export const putOrganizationSettings = (
  transport: MountayaTransport,
  args: {
    readonly organization_id: string;
    readonly body: operations["putOrganizationSettings"]["requestBody"] extends {
      content: { "application/json": infer B };
    }
      ? B
      : never;
    /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
    readonly idempotencyKey?: string | undefined;
    /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
    readonly ifMatch?: string | undefined;
  },
  options?: RequestOptions,
): Promise<SuccessBody<operations["putOrganizationSettings"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PUT",
    operationId: "putOrganizationSettings",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/settings`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * bind returns every 'console' operation with the transport applied.
 */
export const bind = (transport: MountayaTransport) => ({
  admin: {
    keys: {
      /**
       * Revoke a secret key.
       * DELETE /v1/organizations/{organization_id}/secret-keys/{key_id}
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link deleteOrganizationSecretKey}
       */
      deleteOrganizationSecretKey: (
        args: Parameters<typeof deleteOrganizationSecretKey>[1],
        options?: RequestOptions,
      ) => deleteOrganizationSecretKey(transport, args, options),
      /**
       * List all secret keys for the organization.
       * GET /v1/organizations/{organization_id}/secret-keys
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getOrganizationSecretKeys}
       */
      getOrganizationSecretKeys: (
        args: Parameters<typeof getOrganizationSecretKeys>[1],
        options?: RequestOptions,
      ) => getOrganizationSecretKeys(transport, args, options),
      /**
       * Update a secret key name.
       * PATCH /v1/organizations/{organization_id}/secret-keys/{key_id}
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link patchOrganizationSecretKey}
       */
      patchOrganizationSecretKey: (
        args: Parameters<typeof patchOrganizationSecretKey>[1],
        options?: RequestOptions,
      ) => patchOrganizationSecretKey(transport, args, options),
      /**
       * Create a new secret key.
       * POST /v1/organizations/{organization_id}/secret-keys
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link postOrganizationSecretKey}
       */
      postOrganizationSecretKey: (
        args: Parameters<typeof postOrganizationSecretKey>[1],
        options?: RequestOptions,
      ) => postOrganizationSecretKey(transport, args, options),
    },
    members: {
      /**
       * Hand an organization's ownership to another member.
       * POST /v1/organizations/{organization_id}/transfer-ownership
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link postOrganizationTransferOwnership}
       */
      postOrganizationTransferOwnership: (
        args: Parameters<typeof postOrganizationTransferOwnership>[1],
        options?: RequestOptions,
      ) => postOrganizationTransferOwnership(transport, args, options),
    },
    organizations: {
      /**
       * Read the account behind an organization.
       * GET /v1/organizations/{organization_id}/account
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getOrganizationAccount}
       */
      getOrganizationAccount: (
        args: Parameters<typeof getOrganizationAccount>[1],
        options?: RequestOptions,
      ) => getOrganizationAccount(transport, args, options),
      /**
       * Retrieve an organization's entitlement record (staff only).
       * GET /v1/organizations/{organization_id}/entitlements
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getOrganizationEntitlements}
       */
      getOrganizationEntitlements: (
        args: Parameters<typeof getOrganizationEntitlements>[1],
        options?: RequestOptions,
      ) => getOrganizationEntitlements(transport, args, options),
      /**
       * Search every customer organization (staff only).
       * GET /v1/staff/organizations
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getStaffOrganizations}
       */
      getStaffOrganizations: (
        args?: Parameters<typeof getStaffOrganizations>[1],
        options?: RequestOptions,
      ) => getStaffOrganizations(transport, args, options),
      /**
       * Tell whether the signed-in user is a Mountaya staff member.
       * GET /v1/staff/status
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getStaffStatus}
       */
      getStaffStatus: (options?: RequestOptions) => getStaffStatus(transport, options),
      /**
       * Replace an organization's entitlement override (staff only).
       * PUT /v1/organizations/{organization_id}/entitlements
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link putOrganizationEntitlements}
       */
      putOrganizationEntitlements: (
        args: Parameters<typeof putOrganizationEntitlements>[1],
        options?: RequestOptions,
      ) => putOrganizationEntitlements(transport, args, options),
      /**
       * Replace an organization's controlled settings (staff only).
       * PUT /v1/organizations/{organization_id}/settings
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link putOrganizationSettings}
       */
      putOrganizationSettings: (
        args: Parameters<typeof putOrganizationSettings>[1],
        options?: RequestOptions,
      ) => putOrganizationSettings(transport, args, options),
    },
    users: {
      /**
       * Read what the signed-in user may use of Map Studio in their personal space.
       * GET /v1/me/personal-space
       * Requires the session of a person signed in to Mountaya's web application, and acts as that person; a publishable key is refused.
       * @see {@link getMyPersonalSpace}
       */
      getMyPersonalSpace: (options?: RequestOptions) => getMyPersonalSpace(transport, options),
    },
  },
});
